import React, { createContext, useContext, useState, useEffect } from 'react';
import backgroundTranslatorService from '../services/backgroundTranslatorService';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isPageTranslating, setIsPageTranslating] = useState(false);

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  const changeLanguage = (newLanguage) => {
    const previousLanguage = language;
    setLanguage(newLanguage);
    localStorage.setItem('selectedLanguage', newLanguage);
    
    // Start background translation of other pages when language changes
    if (newLanguage !== 'en' && newLanguage !== previousLanguage) {
      console.log(`[Language Context] Language changed to ${newLanguage}, starting background translation...`);
      // Small delay to let current page translation start first
      setTimeout(() => {
        backgroundTranslatorService.startBackgroundTranslation(newLanguage, previousLanguage);
      }, 2000);
    }
  };

  const translations = {
    en: {
      home: 'Home',
      shop: 'Shop',
      about: 'About Us',
      contact: 'Contact',
      myAccount: 'My Account',
      cart: 'Cart',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      logout: 'Logout',
      search: 'Search',
      categories: 'Categories',
      himalayanPinkSalt: 'Himalayan Pink Salt',
      onyxMarbleGranite: 'Onyx Marble Granite',
      lapisAndOthers: 'Lapis & Others',
      apparel: 'Apparel',
      quickLinks: 'Quick Links',
      termsAndConditions: 'Terms & Conditions',
      privacyPolicy: 'Privacy Policy',
      paymentTerms: 'Payment Terms Policy',
      shippingOptions: 'Shipping Options',
      deliveryTimes: 'Delivery Times',
      returnPolicy: 'Return Policy',
      refundPolicy: 'Refund Policy',
      copyright: 'All rights reserved.',
      mineralsRoute: 'Nova Concepts',
      mineralRoutes: 'Nova Concepts',
      // Product page translations
      products: 'Products',
      addToCart: 'Add to Cart',
      addToWishlist: 'Add to Wishlist',
      addToComparison: 'Add to Comparison',
      removeFromComparison: 'Remove from Comparison',
      viewDetails: 'View Details',
      price: 'Price',
      quantity: 'Quantity',
      inStock: 'In Stock',
      outOfStock: 'Out of Stock',
      description: 'Description',
      features: 'Features',
      specifications: 'Specifications',
      // Cart and checkout
      shoppingCart: 'Shopping Cart',
      checkout: 'Checkout',
      total: 'Total',
      subtotal: 'Subtotal',
      shipping: 'Shipping',
      tax: 'Tax',
      proceedToCheckout: 'Proceed to Checkout',
      continueShopping: 'Continue Shopping',
      removeItem: 'Remove Item',
      updateQuantity: 'Update Quantity',
      // Wishlist
      wishlist: 'Wishlist',
      myWishlist: 'My Wishlist',
      clearWishlist: 'Clear Wishlist',
      // Comparison
      comparison: 'Comparison',
      compareProducts: 'Compare Products',
      clearComparison: 'Clear Comparison',
      // Account
      orders: 'Orders',
      orderHistory: 'Order History',
      profile: 'Profile',
      settings: 'Settings',
      // Common actions
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      confirm: 'Confirm',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      // Navigation
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      close: 'Close',
      // Forms
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      city: 'City',
      country: 'Country',
      zipCode: 'ZIP Code',
      message: 'Message',
      subject: 'Subject',
      submit: 'Submit',
      // Company info
      companyName: 'Nova Concepts',
      companyDescription: 'Your trusted partner for premium minerals and stones',
      aboutUsDescription: 'We are a leading supplier of high-quality minerals, stones, and related products.',
      contactUsDescription: 'Get in touch with us for any inquiries or support.',
      // Policies
      termsDescription: 'Please read our terms and conditions carefully.',
      privacyDescription: 'We respect your privacy and protect your personal information.',
      returnDescription: 'Learn about our return and exchange policies.',
      shippingDescription: 'Information about our shipping options and delivery times.'
    },
    es: {
      home: 'Inicio',
      shop: 'Tienda',
      about: 'Acerca de',
      contact: 'Contacto',
      myAccount: 'Mi Cuenta',
      cart: 'Carrito',
      signIn: 'Iniciar Sesión',
      signUp: 'Registrarse',
      logout: 'Cerrar Sesión',
      search: 'Buscar',
      categories: 'Categorías',
      himalayanPinkSalt: 'Sal Rosa del Himalaya',
      onyxMarbleGranite: 'Ónix Mármol Granito',
      lapisAndOthers: 'Lapis y Otros',
      apparel: 'Ropa',
      quickLinks: 'Enlaces Rápidos',
      termsAndConditions: 'Términos y Condiciones',
      privacyPolicy: 'Política de Privacidad',
      paymentTerms: 'Términos de Pago',
      shippingOptions: 'Opciones de Envío',
      deliveryTimes: 'Tiempos de Entrega',
      returnPolicy: 'Política de Devolución',
      refundPolicy: 'Política de Reembolso',
      copyright: 'Todos los derechos reservados.',
      mineralsRoute: 'Nova Concepts',
      mineralRoutes: 'Nova Concepts',
      // Product page translations
      products: 'Productos',
      addToCart: 'Agregar al Carrito',
      addToWishlist: 'Agregar a Favoritos',
      addToComparison: 'Agregar a Comparación',
      removeFromComparison: 'Quitar de Comparación',
      viewDetails: 'Ver Detalles',
      price: 'Precio',
      quantity: 'Cantidad',
      inStock: 'En Stock',
      outOfStock: 'Agotado',
      description: 'Descripción',
      features: 'Características',
      specifications: 'Especificaciones',
      // Cart and checkout
      shoppingCart: 'Carrito de Compras',
      checkout: 'Finalizar Compra',
      total: 'Total',
      subtotal: 'Subtotal',
      shipping: 'Envío',
      tax: 'Impuesto',
      proceedToCheckout: 'Proceder al Pago',
      continueShopping: 'Continuar Comprando',
      removeItem: 'Eliminar Artículo',
      updateQuantity: 'Actualizar Cantidad',
      // Wishlist
      wishlist: 'Lista de Deseos',
      myWishlist: 'Mi Lista de Deseos',
      clearWishlist: 'Limpiar Lista de Deseos',
      // Comparison
      comparison: 'Comparación',
      compareProducts: 'Comparar Productos',
      clearComparison: 'Limpiar Comparación',
      // Account
      orders: 'Pedidos',
      orderHistory: 'Historial de Pedidos',
      profile: 'Perfil',
      settings: 'Configuración',
      // Common actions
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      delete: 'Eliminar',
      confirm: 'Confirmar',
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      warning: 'Advertencia',
      // Navigation
      back: 'Atrás',
      next: 'Siguiente',
      previous: 'Anterior',
      close: 'Cerrar',
      // Forms
      name: 'Nombre',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      address: 'Dirección',
      city: 'Ciudad',
      country: 'País',
      zipCode: 'Código Postal',
      message: 'Mensaje',
      subject: 'Asunto',
      submit: 'Enviar',
      // Company info
      companyName: 'Nova Concepts',
      companyDescription: 'Su socio de confianza para minerales y piedras premium',
      aboutUsDescription: 'Somos un proveedor líder de minerales, piedras y productos relacionados de alta calidad.',
      contactUsDescription: 'Póngase en contacto con nosotros para cualquier consulta o soporte.',
      // Policies
      termsDescription: 'Por favor lea nuestros términos y condiciones cuidadosamente.',
      privacyDescription: 'Respetamos su privacidad y protegemos su información personal.',
      returnDescription: 'Conozca nuestras políticas de devolución e intercambio.',
      shippingDescription: 'Información sobre nuestras opciones de envío y tiempos de entrega.'
    },
    fr: {
      home: 'Accueil',
      shop: 'Boutique',
      about: 'À Propos',
      contact: 'Contact',
      myAccount: 'Mon Compte',
      cart: 'Panier',
      signIn: 'Se Connecter',
      signUp: "S'inscrire",
      logout: 'Se Déconnecter',
      search: 'Rechercher',
      categories: 'Catégories',
      himalayanPinkSalt: 'Sel Rose de l\'Himalaya',
      onyxMarbleGranite: 'Onyx Marbre Granit',
      lapisAndOthers: 'Lapis et Autres',
      apparel: 'Vêtements',
      quickLinks: 'Liens Rapides',
      termsAndConditions: 'Termes et Conditions',
      privacyPolicy: 'Politique de Confidentialité',
      paymentTerms: 'Conditions de Paiement',
      shippingOptions: 'Options d\'Expédition',
      deliveryTimes: 'Délais de Livraison',
      returnPolicy: 'Politique de Retour',
      refundPolicy: 'Politique de Remboursement',
      copyright: 'Tous droits réservés.',
      mineralsRoute: 'Nova Concepts',
      mineralRoutes: 'Nova Concepts',
      // Product page translations
      products: 'Produits',
      addToCart: 'Ajouter au Panier',
      addToWishlist: 'Ajouter aux Favoris',
      addToComparison: 'Ajouter à la Comparaison',
      removeFromComparison: 'Retirer de la Comparaison',
      viewDetails: 'Voir les Détails',
      price: 'Prix',
      quantity: 'Quantité',
      inStock: 'En Stock',
      outOfStock: 'Rupture de Stock',
      description: 'Description',
      features: 'Caractéristiques',
      specifications: 'Spécifications',
      // Cart and checkout
      shoppingCart: 'Panier d\'Achat',
      checkout: 'Commande',
      total: 'Total',
      subtotal: 'Sous-total',
      shipping: 'Expédition',
      tax: 'Taxe',
      proceedToCheckout: 'Procéder à la Commande',
      continueShopping: 'Continuer les Achats',
      removeItem: 'Supprimer l\'Article',
      updateQuantity: 'Mettre à Jour la Quantité',
      // Wishlist
      wishlist: 'Liste de Souhaits',
      myWishlist: 'Ma Liste de Souhaits',
      clearWishlist: 'Vider la Liste de Souhaits',
      // Comparison
      comparison: 'Comparaison',
      compareProducts: 'Comparer les Produits',
      clearComparison: 'Vider la Comparaison',
      // Account
      orders: 'Commandes',
      orderHistory: 'Historique des Commandes',
      profile: 'Profil',
      settings: 'Paramètres',
      // Common actions
      save: 'Enregistrer',
      cancel: 'Annuler',
      edit: 'Modifier',
      delete: 'Supprimer',
      confirm: 'Confirmer',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      warning: 'Avertissement',
      // Navigation
      back: 'Retour',
      next: 'Suivant',
      previous: 'Précédent',
      close: 'Fermer',
      // Forms
      name: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      address: 'Adresse',
      city: 'Ville',
      country: 'Pays',
      zipCode: 'Code Postal',
      message: 'Message',
      subject: 'Sujet',
      submit: 'Soumettre',
      // Company info
      companyName: 'Nova Concepts',
      companyDescription: 'Votre partenaire de confiance pour les minéraux et pierres premium',
      aboutUsDescription: 'Nous sommes un fournisseur leader de minéraux, pierres et produits connexes de haute qualité.',
      contactUsDescription: 'Contactez-nous pour toute question ou support.',
      // Policies
      termsDescription: 'Veuillez lire attentivement nos termes et conditions.',
      privacyDescription: 'Nous respectons votre vie privée et protégeons vos informations personnelles.',
      returnDescription: 'Découvrez nos politiques de retour et d\'échange.',
      shippingDescription: 'Informations sur nos options d\'expédition et délais de livraison.'
    },
    de: {
      home: 'Startseite',
      shop: 'Shop',
      about: 'Über Uns',
      contact: 'Kontakt',
      myAccount: 'Mein Konto',
      cart: 'Warenkorb',
      signIn: 'Anmelden',
      signUp: 'Registrieren',
      logout: 'Abmelden',
      search: 'Suchen',
      categories: 'Kategorien',
      himalayanPinkSalt: 'Himalaya-Rosensalz',
      onyxMarbleGranite: 'Onyx Marmor Granit',
      lapisAndOthers: 'Lapis und Andere',
      apparel: 'Bekleidung',
      quickLinks: 'Schnelle Links',
      termsAndConditions: 'Geschäftsbedingungen',
      privacyPolicy: 'Datenschutzrichtlinie',
      paymentTerms: 'Zahlungsbedingungen',
      shippingOptions: 'Versandoptionen',
      deliveryTimes: 'Lieferzeiten',
      returnPolicy: 'Rückgaberichtlinie',
      refundPolicy: 'Erstattungsrichtlinie',
      copyright: 'Alle Rechte vorbehalten.',
      mineralsRoute: 'Nova Concepts',
      mineralRoutes: 'Nova Concepts',
      // Product page translations
      products: 'Produkte',
      addToCart: 'In den Warenkorb',
      addToWishlist: 'Zu Favoriten hinzufügen',
      addToComparison: 'Zur Vergleich hinzufügen',
      removeFromComparison: 'Aus Vergleich entfernen',
      viewDetails: 'Details anzeigen',
      price: 'Preis',
      quantity: 'Menge',
      inStock: 'Auf Lager',
      outOfStock: 'Nicht verfügbar',
      description: 'Beschreibung',
      features: 'Eigenschaften',
      specifications: 'Spezifikationen',
      // Cart and checkout
      shoppingCart: 'Einkaufswagen',
      checkout: 'Zur Kasse',
      total: 'Gesamt',
      subtotal: 'Zwischensumme',
      shipping: 'Versand',
      tax: 'Steuer',
      proceedToCheckout: 'Zur Kasse gehen',
      continueShopping: 'Einkauf fortsetzen',
      removeItem: 'Artikel entfernen',
      updateQuantity: 'Menge aktualisieren',
      // Wishlist
      wishlist: 'Wunschliste',
      myWishlist: 'Meine Wunschliste',
      clearWishlist: 'Wunschliste leeren',
      // Comparison
      comparison: 'Vergleich',
      compareProducts: 'Produkte vergleichen',
      clearComparison: 'Vergleich leeren',
      // Account
      orders: 'Bestellungen',
      orderHistory: 'Bestellhistorie',
      profile: 'Profil',
      settings: 'Einstellungen',
      // Common actions
      save: 'Speichern',
      cancel: 'Abbrechen',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      confirm: 'Bestätigen',
      loading: 'Laden...',
      error: 'Fehler',
      success: 'Erfolg',
      warning: 'Warnung',
      // Navigation
      back: 'Zurück',
      next: 'Weiter',
      previous: 'Vorherige',
      close: 'Schließen',
      // Forms
      name: 'Name',
      email: 'E-Mail',
      phone: 'Telefon',
      address: 'Adresse',
      city: 'Stadt',
      country: 'Land',
      zipCode: 'Postleitzahl',
      message: 'Nachricht',
      subject: 'Betreff',
      submit: 'Absenden',
      // Company info
      companyName: 'Nova Concepts',
      companyDescription: 'Ihr vertrauensvoller Partner für Premium-Mineralien und Steine',
      aboutUsDescription: 'Wir sind ein führender Anbieter von hochwertigen Mineralien, Steinen und verwandten Produkten.',
      contactUsDescription: 'Kontaktieren Sie uns für Anfragen oder Support.',
      // Policies
      termsDescription: 'Bitte lesen Sie unsere Geschäftsbedingungen sorgfältig durch.',
      privacyDescription: 'Wir respektieren Ihre Privatsphäre und schützen Ihre persönlichen Daten.',
      returnDescription: 'Erfahren Sie mehr über unsere Rückgabe- und Umtauschrichtlinien.',
      shippingDescription: 'Informationen über unsere Versandoptionen und Lieferzeiten.'
    },
    ar: {
      home: 'الرئيسية',
      shop: 'المتجر',
      about: 'من نحن',
      contact: 'اتصل بنا',
      myAccount: 'حسابي',
      cart: 'السلة',
      signIn: 'تسجيل الدخول',
      signUp: 'إنشاء حساب',
      logout: 'تسجيل الخروج',
      search: 'بحث',
      categories: 'الفئات',
      himalayanPinkSalt: 'ملح الهيمالايا الوردي',
      onyxMarbleGranite: 'العقيق الرخام الجرانيت',
      lapisAndOthers: 'اللازورد وغيرها',
      apparel: 'الملابس',
      quickLinks: 'روابط سريعة',
      termsAndConditions: 'الشروط والأحكام',
      privacyPolicy: 'سياسة الخصوصية',
      paymentTerms: 'شروط الدفع',
      shippingOptions: 'خيارات الشحن',
      deliveryTimes: 'أوقات التسليم',
      returnPolicy: 'سياسة الإرجاع',
      refundPolicy: 'سياسة الاسترداد',
      copyright: 'جميع الحقوق محفوظة.',
      mineralsRoute: 'Nova Concepts',
      mineralRoutes: 'Nova Concepts',
      // Product page translations
      products: 'المنتجات',
      addToCart: 'أضف إلى السلة',
      addToWishlist: 'أضف إلى المفضلة',
      addToComparison: 'أضف للمقارنة',
      removeFromComparison: 'إزالة من المقارنة',
      viewDetails: 'عرض التفاصيل',
      price: 'السعر',
      quantity: 'الكمية',
      inStock: 'متوفر',
      outOfStock: 'غير متوفر',
      description: 'الوصف',
      features: 'المميزات',
      specifications: 'المواصفات',
      // Cart and checkout
      shoppingCart: 'سلة التسوق',
      checkout: 'الدفع',
      total: 'المجموع',
      subtotal: 'المجموع الفرعي',
      shipping: 'الشحن',
      tax: 'الضريبة',
      proceedToCheckout: 'المتابعة للدفع',
      continueShopping: 'متابعة التسوق',
      removeItem: 'إزالة العنصر',
      updateQuantity: 'تحديث الكمية',
      // Wishlist
      wishlist: 'قائمة الأمنيات',
      myWishlist: 'قائمة أمنياتي',
      clearWishlist: 'مسح قائمة الأمنيات',
      // Comparison
      comparison: 'المقارنة',
      compareProducts: 'مقارنة المنتجات',
      clearComparison: 'مسح المقارنة',
      // Account
      orders: 'الطلبات',
      orderHistory: 'تاريخ الطلبات',
      profile: 'الملف الشخصي',
      settings: 'الإعدادات',
      // Common actions
      save: 'حفظ',
      cancel: 'إلغاء',
      edit: 'تعديل',
      delete: 'حذف',
      confirm: 'تأكيد',
      loading: 'جاري التحميل...',
      error: 'خطأ',
      success: 'نجح',
      warning: 'تحذير',
      // Navigation
      back: 'رجوع',
      next: 'التالي',
      previous: 'السابق',
      close: 'إغلاق',
      // Forms
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      address: 'العنوان',
      city: 'المدينة',
      country: 'البلد',
      zipCode: 'الرمز البريدي',
      message: 'الرسالة',
      subject: 'الموضوع',
      submit: 'إرسال',
      // Company info
      companyName: 'Nova Concepts',
      companyDescription: 'شريكك الموثوق للمعادن والأحجار الكريمة المتميزة',
      aboutUsDescription: 'نحن مورد رائد للمعادن والأحجار والمنتجات ذات الصلة عالية الجودة.',
      contactUsDescription: 'تواصل معنا لأي استفسارات أو دعم.',
      // Policies
      termsDescription: 'يرجى قراءة شروطنا وأحكامنا بعناية.',
      privacyDescription: 'نحن نحترم خصوصيتك ونحمي معلوماتك الشخصية.',
      returnDescription: 'تعرف على سياسات الإرجاع والاستبدال الخاصة بنا.',
      shippingDescription: 'معلومات حول خيارات الشحن وأوقات التسليم.'
    }
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const value = {
    language,
    changeLanguage,
    t,
    translations,
    isPageTranslating,
    setIsPageTranslating
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
